import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { useCallback, useEffect, useState } from "react"
import {
    selectContactListById,
    selectContactListsArray,
    selectHasSetContactsForWorkspaceId,
    setContactLists,
} from "./slice"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { MissionGroupColor, MissionGroupIcon, ShareContact } from "@nova/schemas"

export function useContactListFunctions() {
    const dispatch = useAppDispatch()
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const contactLists = useAppSelector(selectContactListsArray)
    const [isFetching, setIsFetching] = useState(false)
    const hasSetForWorkspaceId = useAppSelector(selectHasSetContactsForWorkspaceId)
    const getAllLists = useCallback(async () => {
        if (!workspaceId) return
        if (hasSetForWorkspaceId === workspaceId) return { contactLists }
        setIsFetching(true)
        try {
            const { contactLists: apiContactLists } = await fetchApiGateway(
                "get /org/:orgId/share-contacts",
                {
                    params: {
                        orgId: workspaceId,
                    },
                }
            )
            dispatch(setContactLists({ contactLists: apiContactLists, workspaceId }))
            return { contactLists: apiContactLists }
        } catch {
            return { contactLists: [] }
        } finally {
            setIsFetching(false)
        }
    }, [contactLists, dispatch, hasSetForWorkspaceId, workspaceId])
    const createList = useCallback(
        (
            name: string,
            contacts: ShareContact[],
            color: MissionGroupColor,
            icon: MissionGroupIcon
        ) => {
            if (!workspaceId) return
            return fetchApiGateway("put /org/:orgId/share-contacts", {
                params: {
                    orgId: workspaceId,
                },
                body: {
                    name,
                    contacts,
                    color,
                    icon,
                },
            })
        },
        [workspaceId]
    )
    const updateList = useCallback(
        (
            listId: string,
            name: string,
            contacts: ShareContact[],
            color: MissionGroupColor,
            icon: MissionGroupIcon,
            autoNotify?: boolean,
            passwordEnabled?: boolean,
            password?: string
        ) => {
            if (!workspaceId) return
            const contactList = selectContactListById(listId)(store.getState())
            if (!contactList) return
            return fetchApiGateway("patch /org/:orgId/share-contacts", {
                params: {
                    orgId: workspaceId,
                },
                body: {
                    listId: contactList.id,
                    name,
                    contacts,
                    color,
                    icon,
                    autoNotify,
                    passwordEnabled,
                    password,
                },
            })
        },
        [workspaceId]
    )
    const deleteList = useCallback(
        (listId: string) => {
            if (!workspaceId) return
            return fetchApiGateway("delete /org/:orgId/share-contacts", {
                params: {
                    orgId: workspaceId,
                },
                body: {
                    listId,
                },
            })
        },
        [workspaceId]
    )
    return {
        getAllLists,
        isFetching,
        createList,
        updateList,
        deleteList,
    }
}

export const useContactLists = () => {
    const { getAllLists, isFetching } = useContactListFunctions()
    const contactLists = useAppSelector(selectContactListsArray)
    useEffect(() => {
        getAllLists()
    }, [getAllLists])
    return {
        contactLists,
        isFetching,
    }
}
